import styles from '../booking-form.module.scss';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import VoiceInput from '@/components/HeroBlockBlog/components/VoiceInput/VoiceInput';
import PhoneInput from '@/components/HeroBlockBlog/components/PhoneInput/PhoneInput';
import ButtonSimple from '@/components/HeroBlockBlog/components/ButtonSimple/ButtonSimple';
import { BookingFormData, CalculatePriceResponse, AircraftListItem, FlightSummary } from '@/types/heroBlock.types';
import { jetCatalogItems } from '@/data/jetCatalog.data';
import { useState } from 'react';
import CheckboxForForm from '@/components/CheckboxForForm/CheckboxForForm';

interface CurrentTab2Props {
  control: Control<BookingFormData>;
  errors: FieldErrors<BookingFormData>;
  handleSubmitQuote: () => Promise<void>;
  isSubmitting: boolean;
  priceData?: CalculatePriceResponse | null;
  flightSummary?: FlightSummary | null;
}

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// "Super Mid-Size Jet" → "super_mid_size_jet"
const toApiCategoryKey = (typeAircraft: string): string =>
  typeAircraft.toLowerCase().replace(/[-\s]+/g, '_');

const getPriceForCategory = (
  priceData: CalculatePriceResponse | null | undefined,
  typeAircraft: string
): number | null => {
  if (!priceData?.prices) return null;
  const key = toApiCategoryKey(typeAircraft);
  return priceData.prices[key] ?? null;
};

const getAircraftForCategory = (
  priceData: CalculatePriceResponse | null | undefined,
  typeAircraft: string
): AircraftListItem[] => {
  if (!priceData?.aircraft_list) return [];
  const key = toApiCategoryKey(typeAircraft);
  return priceData.aircraft_list.filter((a) => a.category_code === key);
};

interface DisplayAircraft {
  image: string;
  name: string;
  tail: string;
  price: number | null;
  isFromApi: boolean;
}

const categoryKeyToLabel: Record<string, string> = {
  light_jet: 'Light Jet',
  mid_size_jet: 'Mid-Size Jet',
  super_mid_size_jet: 'Super Mid-Size Jet',
  heavy_jet: 'Heavy Jet',
};

const buildDisplayAircrafts = (
  apiAircrafts: AircraftListItem[],
  typeAircraft: string,
  categoryAvgPrice: number | null
): DisplayAircraft[] => {
  const catalogByTail = new Map(jetCatalogItems.map((j) => [j.tail, j]));
  const result: DisplayAircraft[] = [];
  const seenTails = new Set<string>();
  const seenImages = new Set<string>();

  // First: match API aircraft to catalog images with individual prices
  for (const aircraft of apiAircrafts) {
    if (result.length >= 3) break;
    if (!aircraft.tail_number) continue;
    const catalogItem = catalogByTail.get(aircraft.tail_number);
    if (catalogItem && !seenImages.has(catalogItem.image)) {
      result.push({
        image: catalogItem.image,
        name: catalogItem.name,
        tail: aircraft.tail_number,
        price: aircraft.total_price,
        isFromApi: true,
      });
      seenTails.add(aircraft.tail_number);
      seenImages.add(catalogItem.image);
    }
  }

  // Fill up to 3 with catalog fallbacks at category avg price
  if (result.length < 3) {
    const categoryKey = toApiCategoryKey(typeAircraft);
    const catalogCategory = categoryKeyToLabel[categoryKey] ?? typeAircraft;

    const sameCategoryFallbacks = jetCatalogItems.filter(
      (j) => j.category === catalogCategory && !seenTails.has(j.tail) && !seenImages.has(j.image)
    );

    for (const fallback of sameCategoryFallbacks) {
      if (result.length >= 3) break;
      result.push({
        image: fallback.image,
        name: fallback.name,
        tail: fallback.tail,
        price: categoryAvgPrice,
        isFromApi: false,
      });
      seenTails.add(fallback.tail);
      seenImages.add(fallback.image);
    }

    // Special case: Light Jet has only 1 catalog entry — supplement with Mid-Size Jet
    if (result.length < 3 && catalogCategory === 'Light Jet') {
      const usedTails2 = new Set(result.map((j) => j.tail));
      const usedImages2 = new Set(result.map((j) => j.image));
      const midSizeFallbacks = jetCatalogItems.filter(
        (j) => j.category === 'Mid-Size Jet' && !usedTails2.has(j.tail) && !usedImages2.has(j.image)
      );
      for (const fallback of midSizeFallbacks) {
        if (result.length >= 3) break;
        result.push({
          image: fallback.image,
          name: fallback.name,
          tail: fallback.tail,
          price: categoryAvgPrice,
          isFromApi: false,
        });
      }
    }
  }

  return result;
};

const formatPrice = (p: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(p);

const FlightSummaryCard = ({
  priceData,
  flightSummary,
}: {
  priceData: CalculatePriceResponse | null | undefined;
  flightSummary: FlightSummary | null | undefined;
}) => {
  if (!flightSummary) return null;

  const isMultiCity = flightSummary.activeTab === 'multi-city';
  const isRoundTrip = flightSummary.activeTab === 'round-trip';

  const categoryAvgPrice = getPriceForCategory(priceData, flightSummary.typeAircraft);
  const matchedAircrafts = getAircraftForCategory(priceData, flightSummary.typeAircraft);
  const categoryName = matchedAircrafts[0]?.category_name ?? flightSummary.typeAircraft;

  const displayAircrafts = buildDisplayAircrafts(
    matchedAircrafts,
    flightSummary.typeAircraft,
    categoryAvgPrice
  );

  // Price range based on real API aircraft prices only
  const apiPrices = matchedAircrafts
    .map((a) => a.total_price)
    .filter((p) => p > 0)
    .sort((a, b) => a - b);

  let priceDisplay: string | null = null;
  if (apiPrices.length === 0 && categoryAvgPrice) {
    priceDisplay = formatPrice(categoryAvgPrice);
  } else if (apiPrices.length === 1) {
    priceDisplay = formatPrice(apiPrices[0]);
  } else if (apiPrices.length > 1) {
    const min = apiPrices[0];
    const max = apiPrices[apiPrices.length - 1];
    priceDisplay = min === max ? formatPrice(min) : `${formatPrice(min)} – ${formatPrice(max)}`;
  }

  return (
    <div className={styles.flightSummaryCard}>
      <div className={styles.flightSummaryHeader}>
        <span className={styles.flightSummaryTitle}>Flight Summary</span>
        <span className={styles.flightSummaryJetCategory}>{categoryName}</span>
      </div>

      {/* Route */}
      <div className={styles.flightSummaryRoute}>
        {isMultiCity ? (
          <div className={styles.flightSummaryLegs}>
            {flightSummary.multiCityInputs.slice(0, -1).map((fromCity, legIndex) => {
              const toCity = flightSummary.multiCityInputs[legIndex + 1];
              const date = flightSummary.multiCityDates?.[legIndex] ?? '';
              const time = flightSummary.multiCityTimes?.[legIndex] ?? '';
              return (
                <div key={legIndex}>
                  {legIndex > 0 && <div className={styles.flightSummaryLegDivider} />}
                  <div className={styles.flightSummaryFromTo}>
                    <div className={styles.flightSummaryPoint}>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src="/img/heroBlock/form/trip1.svg"
                        alt="departure"
                        className={styles.flightSummaryPlaneIcon}
                      />
                      <span className={styles.flightSummaryCity}>{fromCity}</span>
                      {date && (
                        <span className={styles.flightSummaryMeta}>
                          {formatDate(date)}
                          {time && ` • ${time}`}
                        </span>
                      )}
                    </div>
                    <div className={styles.flightSummaryLine} />
                    <div className={styles.flightSummaryPoint}>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src="/img/heroBlock/form/trip2.svg"
                        alt="arrival"
                        className={styles.flightSummaryPlaneIcon}
                      />
                      <span className={styles.flightSummaryCity}>{toCity}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.flightSummaryLegs}>
            {/* Outbound leg */}
            <div className={styles.flightSummaryFromTo}>
              <div className={styles.flightSummaryPoint}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/img/heroBlock/form/trip1.svg"
                  alt="departure"
                  className={styles.flightSummaryPlaneIcon}
                />
                <span className={styles.flightSummaryCity}>
                  {flightSummary.fromLocation}
                </span>
                <span className={styles.flightSummaryMeta}>
                  {formatDate(flightSummary.departureDate)}
                  {flightSummary.departureTime && ` • ${flightSummary.departureTime}`}
                </span>
              </div>
              <div className={styles.flightSummaryLine} />
              <div className={styles.flightSummaryPoint}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/img/heroBlock/form/trip2.svg"
                  alt="arrival"
                  className={styles.flightSummaryPlaneIcon}
                />
                <span className={styles.flightSummaryCity}>
                  {flightSummary.toLocation}
                </span>
              </div>
            </div>

            {/* Return leg — only for round-trip */}
            {isRoundTrip && flightSummary.returnDate && (
              <>
                <div className={styles.flightSummaryLegDivider} />
                <div className={styles.flightSummaryFromTo}>
                  <div className={styles.flightSummaryPoint}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src="/img/heroBlock/form/trip1.svg"
                      alt="departure"
                      className={styles.flightSummaryPlaneIcon}
                    />
                    <span className={styles.flightSummaryCity}>
                      {flightSummary.toLocation}
                    </span>
                    <span className={styles.flightSummaryMeta}>
                      {formatDate(flightSummary.returnDate)}
                      {flightSummary.returnTime && ` • ${flightSummary.returnTime}`}
                    </span>
                  </div>
                  <div className={styles.flightSummaryLine} />
                  <div className={styles.flightSummaryPoint}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src="/img/heroBlock/form/trip2.svg"
                      alt="arrival"
                      className={styles.flightSummaryPlaneIcon}
                    />
                    <span className={styles.flightSummaryCity}>
                      {flightSummary.fromLocation}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Passengers */}
        <div className={styles.flightSummaryPassengers}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span>
            {flightSummary.passengers} passenger
            {parseInt(flightSummary.passengers) !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Aircraft images with individual prices */}
      {displayAircrafts.length > 0 && (
        <div className={styles.flightSummaryImages}>
          {displayAircrafts.map((aircraft) => (
            <div key={aircraft.tail} className={styles.flightSummaryImageCard}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={aircraft.image}
                alt={aircraft.name}
                className={styles.flightSummaryImagePhoto}
              />
              <div className={styles.flightSummaryImageMeta}>
                <div className={styles.flightSummaryImageInfo}>
                  <span className={styles.flightSummaryImageName}>{aircraft.name}</span>
                  <span className={styles.flightSummaryImageTail}>{aircraft.tail}</span>
                </div>
                {aircraft.price != null && (
                  <span className={styles.flightSummaryAircraftPrice}>
                    {formatPrice(aircraft.price)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Price range */}
      {priceDisplay && (
        <div className={styles.flightSummaryPriceRow}>
          <div className={styles.flightSummaryPriceLabel}>
            <span>Total estimated price</span>
            <span className={styles.flightSummaryPriceNote}>
              Prices are estimates only
            </span>
          </div>
          <span className={styles.flightSummaryPrice}>{priceDisplay}</span>
        </div>
      )}

      <div className={styles.flightSummaryReturnPolicy}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span>Free cancellation up to 24 hours</span>
      </div>
    </div>
  );
};

const CurrentTab2 = ({
  control,
  handleSubmitQuote,
  isSubmitting,
  priceData,
  flightSummary,
}: CurrentTab2Props) => {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  const filterCyrillicFromEmail = (value: string) => {
    return value.replace(/[а-яё]/gi, '');
  };

  const handleSubmitWithValidation = async () => {
    setSubmitAttempted(true);
    await handleSubmitQuote();
  };
  return (
    <>
      <div className={styles.line}>
        <div className={styles.inputWrapper}>
          <Controller
            name="firstName"
            control={control}
            rules={{ required: 'Please enter your first name' }}
            render={({ field, fieldState }) => (
              <div>
                <VoiceInput
                  label="First name"
                  placeholder="Enter your first name"
                  value={field.value}
                  onChange={field.onChange}
                />
                {(fieldState.isTouched || submitAttempted) &&
                  fieldState.error && (
                    <div className={styles.errorMessage}>
                      {fieldState.error.message}
                    </div>
                  )}
              </div>
            )}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Controller
            name="lastName"
            control={control}
            rules={{ required: 'Please enter your last name' }}
            render={({ field, fieldState }) => (
              <div>
                <VoiceInput
                  label="Last name"
                  placeholder="Enter your last name"
                  value={field.value}
                  onChange={field.onChange}
                />
                {(fieldState.isTouched || submitAttempted) &&
                  fieldState.error && (
                    <div className={styles.errorMessage}>
                      {fieldState.error.message}
                    </div>
                  )}
              </div>
            )}
          />
        </div>

        <div className={styles.inputWrapper}>
          <Controller
            name="email"
            control={control}
            rules={{
              required: 'Please enter your email',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Please enter a valid email address',
              },
            }}
            render={({ field, fieldState }) => (
              <div>
                <VoiceInput
                  label="Email"
                  placeholder="Enter your email"
                  value={field.value}
                  onChange={(value) =>
                    field.onChange(filterCyrillicFromEmail(value))
                  }
                />
                {(fieldState.isTouched || submitAttempted) &&
                  fieldState.error && (
                    <div className={styles.errorMessage}>
                      {fieldState.error.message}
                    </div>
                  )}
              </div>
            )}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Controller
            name="phone"
            control={control}
            rules={{
              required: 'Please enter your phone number',
              validate: (value) => {
                if (!value || value.trim() === '' || value === '+') {
                  return 'Please enter your phone number';
                }

                if (value.length < 6) {
                  return 'Please enter a complete phone number';
                }

                if (value.length > 19) {
                  return 'Phone number is too long';
                }

                return true;
              },
            }}
            render={({ field, fieldState }) => {
              const shouldShowError =
                (fieldState.isTouched || submitAttempted) && fieldState.error;

              return (
                <div>
                  <PhoneInput
                    label="Phone"
                    placeholder="Enter your phone"
                    value={field.value || ''}
                    onChange={(phoneNumber) => {
                      field.onChange(phoneNumber);
                      if (!fieldState.isTouched) {
                        field.onBlur();
                      }
                    }}
                    error={
                      shouldShowError ? fieldState.error?.message : undefined
                    }
                  />
                </div>
              );
            }}
          />
        </div>
      </div>

      <FlightSummaryCard priceData={priceData} flightSummary={flightSummary} />

      <div className={styles.line2}>
        <CheckboxForForm
          consentChecked={consentChecked}
          setConsentChecked={setConsentChecked}
          maxWidth="600px"
        />
        <ButtonSimple
          color="accent"
          highContrast
          loading={isSubmitting}
          size={3}
          state="default ✦"
          variant="solid ✦"
          text={isSubmitting ? 'Sending...' : 'Get a Quote →'}
          iconStart={false}
          iconEnd={false}
          focusRing={false}
          designBlendingMode="/img/heroBlock/microphone.svg"
          designBlendingMode1="/img/heroBlock/microphone.svg"
          className={styles.nextButton}
          disabled={isSubmitting}
          onClick={handleSubmitWithValidation}
        />
      </div>
    </>
  );
};

export default CurrentTab2;
